import React, { useMemo } from 'react'
import { SellerQueueItem } from '../../contexts/SellerQueueContext'
import './index.css'

export interface SellerQueueCardProps {
  queueEntry: SellerQueueItem | null
  isLoading?: boolean
  error?: Error | null
  elapsedTime?: number
  onEnterQueue?: () => Promise<void>
  onLeaveQueue?: () => Promise<void>
  onStartService?: () => Promise<void>
  onReturnToQueue?: () => void
  onEndService?: () => void
  isServiceActive?: boolean
  isLeaveLoading?: boolean
  isStartServiceLoading?: boolean
}

/**
 * SellerQueueCard
 * Displays seller's position in queue, status, and action buttons
 * Used in both SellerDashboard and SellerAttendance page
 */
export const SellerQueueCard: React.FC<SellerQueueCardProps> = ({
  queueEntry,
  isLoading,
  error,
  elapsedTime,
  onEnterQueue,
  onLeaveQueue,
  onStartService,
  onReturnToQueue,
  onEndService,
  isServiceActive,
  isLeaveLoading,
  isStartServiceLoading,
}) => {
  // Determine status color and label
  const statusInfo = useMemo(() => {
    if (!queueEntry) {
      return { color: 'neutral', label: 'Offline', class: 'status-offline' }
    }

    switch (queueEntry.status) {
      case 'available':
        return { color: 'success', label: 'Disponível', class: 'status-available' }
      case 'paused':
        return { color: 'warning', label: 'Pausado', class: 'status-paused' }
      case 'in_service':
        return { color: 'info', label: 'Em Atendimento', class: 'status-in-service' }
      case 'offline':
      default:
        return { color: 'neutral', label: 'Offline', class: 'status-offline' }
    }
  }, [queueEntry])

  // Determine which buttons to show based on status
  const isInService = queueEntry?.status === 'in_service' || isServiceActive
  const showEnterButton = !queueEntry
  const showLeaveButton = !!queueEntry && !isInService
  const showStartServiceButton = queueEntry?.status === 'available' && !isServiceActive
  const showReturnToQueueButton = isInService
  const showEndServiceButton = isInService

  const handleEnterQueue = async () => {
    try {
      await onEnterQueue?.()
    } catch (err) {
      console.error('Erro ao entrar na fila:', err)
    }
  }

  const handleLeaveQueue = async () => {
    try {
      await onLeaveQueue?.()
    } catch (err) {
      console.error('Erro ao sair da fila:', err)
    }
  }

  const handleStartService = async () => {
    try {
      await onStartService?.()
    } catch (err) {
      console.error('Erro ao iniciar atendimento:', err)
    }
  }

  const formatBrasiliaTime = (value?: string | null) => {
    if (!value) return '—'

    const date = new Date(value)
    date.setHours(date.getHours() - 3)

    return new Intl.DateTimeFormat('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(date)
  }

  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    const s = seconds % 60

    if (h > 0) return `${h}h ${m}m`
    if (m > 0) return `${m}m ${s}s`
    return `${s}s`
  }

  return (
    <div className="seller-queue-card">
      {/* Header */}
      <div className="card-header">
        <h3>Status da Fila</h3>
        <span className={`status-badge ${statusInfo.class}`}>{statusInfo.label}</span>
      </div>

      {/* Error State */}
      {error && (
        <div className="card-error">
          <p>⚠️ Erro: {error.message}</p>
        </div>
      )}

      {/* Loading State */}
      {isLoading && !queueEntry && (
        <div className="card-loading">
          <div className="spinner"></div>
          <p>Carregando...</p>
        </div>
      )}

      {/* No Queue Entry */}
      {!queueEntry && !isLoading && (
        <div className="card-empty">
          <p>Você não está na fila</p>
        </div>
      )}

      {/* Queue Info */}
      {queueEntry && (
        <div className="card-content">
          <div className="position-display">
            {isInService ? (
              <>
                <div className="position-number">Atendendo</div>
                <div className="position-label">Em atendimento</div>
              </>
            ) : (
              <>
                <div className="position-number">#{queueEntry.position}</div>
                <div className="position-label">Posição na fila</div>
              </>
            )}
          </div>

          <div className="queue-details">
            <div className="detail-row">
              <span className="detail-label">Status do Vendedor:</span>
              <span className={`detail-value ${statusInfo.class}`}>{statusInfo.label}</span>
            </div>
            {typeof elapsedTime === 'number' && isInService && (
              <div className="detail-row">
                <span className="detail-label">Duração:</span>
                <span className="detail-value">{formatTime(elapsedTime)}</span>
              </div>
            )}
            <div className="detail-row">
              <span className="detail-label">Última Atualização:</span>
              <span className="detail-value">{formatBrasiliaTime(queueEntry.updated)}</span>
            </div>
            {queueEntry.lastCalledAt && (
              <div className="detail-row">
                <span className="detail-label">Chamado em:</span>
                <span className="detail-value">{formatBrasiliaTime(queueEntry.lastCalledAt)}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="card-actions">
        {/* Enter Queue Button */}
        {showEnterButton && (
          <button
            className="btn btn-primary btn-full"
            onClick={handleEnterQueue}
            disabled={isLoading}
            title="Entrar na fila de atendimento"
          >
            Entrar na Fila
          </button>
        )}

        {/* Service Buttons Row */}
        {showLeaveButton && (
          <div className="button-row">
            <button
              className="btn btn-danger"
              onClick={handleLeaveQueue}
              disabled={isLeaveLoading}
              title="Sair da fila de atendimento"
            >
              Sair
            </button>
          </div>
        )}

        {/* Start Service Button */}
        {showStartServiceButton && (
          <button
            className="btn btn-success btn-full"
            onClick={handleStartService}
            disabled={isStartServiceLoading}
            title="Iniciar atendimento com cliente"
          >
            Iniciar Atendimento
          </button>
        )}

        {/* Return To Queue Button */}
        {showReturnToQueueButton && (
          <button
            className="btn btn-primary btn-full"
            onClick={onReturnToQueue}
            title="Encerrar atendimento e retornar para a fila"
          >
            Retornar à Fila
          </button>
        )}

        {/* End Service Button */}
        {showEndServiceButton && (
          <button
            className="btn btn-danger btn-full"
            onClick={onEndService}
            title="Encerrar atendimento e sair da fila"
          >
            Encerrar Atendimento
          </button>
        )}
      </div>
    </div>
  )
}
